import { Children, forwardRef, isValidElement } from 'react'
import { ChevronDown } from 'lucide-react'

/** Walk `<option>` children and pull out their text labels — used to
 *  size the wrapper to the longest option. Skips non-`<option>` JSX
 *  (groups, fragments containing nulls, etc.) and ignores option
 *  children that aren't plain strings (no good way to measure JSX). */
function collectOptionLabels(children) {
  const labels = []
  const visit = (nodes) => {
    Children.forEach(nodes, (child) => {
      if (!isValidElement(child)) return
      if (child.type === 'option') {
        const c = child.props.children
        if (typeof c === 'string') labels.push(c)
        else if (typeof c === 'number') labels.push(String(c))
        return
      }
      // Recurse into <optgroup> / fragments so grouped selects size
      // off their inner option labels too.
      if (child.props?.children) visit(child.props.children)
    })
  }
  visit(children)
  return labels
}

/**
 * Native `<select>` with a custom Lucide chevron that **rotates** when
 * the popup opens. Drop-in replacement for `<select>` — accepts the
 * same props (including `{...register('field')}` from react-hook-form
 * via the forwarded ref).
 *
 * How the rotation works: `src/lib/dropdownArrowGlue.js` tracks the
 * native-select open state via a `data-open` attribute (since the
 * browser doesn't fire a JS event when the popup opens), and the
 * `select + svg` / `select ~ svg` rules in `index.css` rotate the
 * sibling chevron 180° on open. Both need to be in place for the
 * arrow to spin.
 *
 * The browser's native dropdown arrow is hidden via
 * `appearance: none` so only our Lucide chevron is visible.
 *
 * Use this in place of a raw `<select>` anywhere we want a
 * consistent rotating arrow — Super Admin filter bars, form
 * dropdowns, modal pickers, etc.
 */
export const Select = forwardRef(function Select(
  {
    className = '',
    wrapperClassName = '',
    children,
    sizeVariant = 'md',
    fullWidth = true,
    ...rest
  },
  ref,
) {
  const height = sizeVariant === 'sm' ? 'h-9' : 'h-10'

  // Placeholder colouring is handled in CSS, not here — see the
  // `.fd-select:has(option[value='']:checked)` rule in `index.css`.
  //
  // A native <select> paints the selected option in normal ink, so a
  // `<option value="">Select a Plan</option>` placeholder rendered as dark as
  // a real answer, while the sibling CountryFilter showed its placeholder in
  // `--color-ink-subtle` — two controls in one form, two different greys
  // (QA-reported on Create Company).
  //
  // CSS rather than state because consumers bind with RHF's `{...register()}`
  // (uncontrolled, no `value` prop) AND call `reset()` imperatively, which
  // fires no change event. `:has()` re-evaluates on every selection change
  // including an imperative one, so it cannot go stale; a JS mirror would
  // have needed a dependency-less effect to keep up.
  //
  // Scoped to `.fd-select` so a raw <select> whose empty option is a REAL
  // choice ("— No folder —", "— None —") keeps full ink.
  // Two layout modes:
  //   - `fullWidth: true`  → block w-full container; select stretches
  //     to fill the row. Right for forms / modals.
  //   - `fullWidth: false` → inline-grid container with one cell that
  //     holds both the select AND a ghost span per option. Grid sizes
  //     the cell to the WIDEST item, so the wrapper auto-sizes to the
  //     longest option label and never truncates — no matter which
  //     option is currently picked.
  const isFullWidth = fullWidth
  const labels = !isFullWidth ? collectOptionLabels(children) : []
  // `wrapperClassName` is exposed so callers can override the layout
  // (e.g. add a min-width, or force `w-full`). Default just sets the
  // display mode appropriate to the chosen size strategy.
  const wrapperLayout = isFullWidth
    ? 'block w-full relative'
    : 'inline-grid relative'
  return (
    <div className={`${wrapperLayout} ${wrapperClassName}`}>
      <select
        ref={ref}
        // In inline-grid mode both the select and the ghost spans
        // share the same grid cell — `gridArea: '1 / 1'`. Grid
        // resolves the cell's width to the widest member, so the
        // select grows with the longest option label automatically.
        style={!isFullWidth ? { gridArea: '1 / 1' } : undefined}
        className={
          `fd-select ${isFullWidth ? 'w-full' : ''} ${height} pl-3 pr-9 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-sm text-[color:var(--color-ink)] cursor-pointer outline-none transition-colors focus:border-[color:var(--color-accent)] appearance-none ` +
          className
        }
        {...rest}
      >
        {children}
      </select>
      {!isFullWidth
        ? labels.map((l, i) => (
            // Ghost — invisible, same padding profile as the real
            // <select>, sits in the same grid cell. Pushes the cell
            // width but contributes zero visible layout. `h-0` keeps
            // it from forcing vertical space; the select retains the
            // chosen `${height}`.
            <span
              key={i}
              aria-hidden="true"
              style={{ gridArea: '1 / 1' }}
              className="invisible pointer-events-none h-0 pl-3 pr-9 text-sm whitespace-nowrap overflow-hidden"
            >
              {l}
            </span>
          ))
        : null}
      <ChevronDown
        size={14}
        aria-hidden="true"
        className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-[color:var(--color-ink-muted)]"
      />
    </div>
  )
})
